import { styled } from '@mui/material/styles'
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area'
import { forwardRef, useCallback, useEffect, useRef } from 'react'

const ScrollAreaRoot = styled(ScrollAreaPrimitive.Root)(({ theme }) => ({
  '--scroll-area-size': '4px',
  '--scroll-area-thumb-color': theme.palette.primary.main,
  '--scroll-area-radius': '9999px',
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
}))

const ScrollAreaViewport = styled(ScrollAreaPrimitive.Viewport)({
  width: '100%',
  height: '100%',
  borderRadius: 'inherit',
})

const Scrollbar = styled(ScrollAreaPrimitive.Scrollbar)({
  display: 'flex',
  touchAction: 'none',
  userSelect: 'none',
  backgroundColor: 'transparent',
  borderRadius: 'var(--scroll-area-radius)',
  '&[data-orientation="vertical"]': {
    width: 'var(--scroll-area-size)',
  },
  '&[data-orientation="horizontal"]': {
    height: 'var(--scroll-area-size)',
    flexDirection: 'column',
  },
})

const ScrollbarThumb = styled(ScrollAreaPrimitive.Thumb)({
  position: 'relative',
  flex: 1,
  backgroundColor: 'var(--scroll-area-thumb-color)',
  borderRadius: 'var(--scroll-area-radius)',
})

const ScrollbarCorner = styled(ScrollAreaPrimitive.Corner)({
  backgroundColor: 'transparent',
})

const setRefValue = (ref, value) => {
  if (typeof ref === 'function') {
    ref(value)
  } else if (ref) {
    ref.current = value
  }
}

export const ScrollArea = forwardRef(function ScrollArea(
  {
    children,
    horizontalScrollbarProps,
    scrollbars = 'both',
    type = 'auto',
    verticalScrollbarProps,
    viewportProps,
    viewportRef,
    ...props
  },
  ref,
) {
  const showHorizontalScrollbar = scrollbars !== 'vertical'
  const showVerticalScrollbar = scrollbars !== 'horizontal'
  const internalViewportRef = useRef(null)
  const dragStateRef = useRef(null)
  const stalePointerIdRef = useRef(null)

  const setViewportRef = useCallback(
    (node) => {
      internalViewportRef.current = node
      setRefValue(viewportRef, node)
    },
    [viewportRef],
  )

  const restoreDragSideEffects = useCallback(() => {
    const dragState = dragStateRef.current

    if (!dragState) return

    dragState.ownerDocument.body.style.webkitUserSelect =
      dragState.bodyWebkitUserSelect

    if (dragState.viewport) {
      dragState.viewport.style.scrollBehavior = dragState.viewportScrollBehavior
    }

    dragStateRef.current = null
  }, [])

  useEffect(() => restoreDragSideEffects, [restoreDragSideEffects])

  const getScrollbarProps = (scrollbarProps = {}) => {
    const {
      onLostPointerCapture,
      onPointerCancel,
      onPointerDown,
      onPointerMove,
      onPointerUp,
      ...rest
    } = scrollbarProps

    return {
      ...rest,
      onPointerDown: (event) => {
        onPointerDown?.(event)

        if (event.defaultPrevented || event.button !== 0) return

        restoreDragSideEffects()
        stalePointerIdRef.current = null

        const viewport = internalViewportRef.current
        const ownerDocument = event.currentTarget.ownerDocument

        dragStateRef.current = {
          bodyWebkitUserSelect: ownerDocument.body.style.webkitUserSelect ?? '',
          ownerDocument,
          pointerId: event.pointerId,
          viewport,
          viewportScrollBehavior: viewport?.style.scrollBehavior ?? '',
        }
      },
      onPointerMove: (event) => {
        onPointerMove?.(event)

        const isCanceledPointer = stalePointerIdRef.current === event.pointerId
        const isReleasedPointer =
          dragStateRef.current?.pointerId === event.pointerId &&
          event.buttons === 0

        if (!isCanceledPointer && !isReleasedPointer) return

        // Radix only clears its drag state on pointerup. Prevent its composed
        // pointermove handler from continuing a canceled or released drag.
        event.preventDefault()
        stalePointerIdRef.current = event.pointerId
        restoreDragSideEffects()
      },
      onPointerUp: (event) => {
        onPointerUp?.(event)

        if (dragStateRef.current?.pointerId !== event.pointerId) return

        if (event.defaultPrevented) {
          stalePointerIdRef.current = event.pointerId
          restoreDragSideEffects()
        } else {
          dragStateRef.current = null
          stalePointerIdRef.current = null
        }
      },
      onPointerCancel: (event) => {
        onPointerCancel?.(event)

        if (dragStateRef.current?.pointerId !== event.pointerId) return

        stalePointerIdRef.current = event.pointerId
        restoreDragSideEffects()
      },
      onLostPointerCapture: (event) => {
        onLostPointerCapture?.(event)

        if (dragStateRef.current?.pointerId !== event.pointerId) return

        stalePointerIdRef.current = event.pointerId
        restoreDragSideEffects()
      },
    }
  }

  const protectedHorizontalScrollbarProps = getScrollbarProps(
    horizontalScrollbarProps,
  )
  const protectedVerticalScrollbarProps = getScrollbarProps(
    verticalScrollbarProps,
  )

  return (
    <ScrollAreaRoot ref={ref} type={type} {...props}>
      <ScrollAreaViewport ref={setViewportRef} {...viewportProps}>
        {children}
      </ScrollAreaViewport>
      {showVerticalScrollbar && (
        <Scrollbar {...protectedVerticalScrollbarProps} orientation="vertical">
          <ScrollbarThumb />
        </Scrollbar>
      )}
      {showHorizontalScrollbar && (
        <Scrollbar
          {...protectedHorizontalScrollbarProps}
          orientation="horizontal"
        >
          <ScrollbarThumb />
        </Scrollbar>
      )}
      {showHorizontalScrollbar && showVerticalScrollbar && <ScrollbarCorner />}
    </ScrollAreaRoot>
  )
})
